import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import AudioEngine from './audioEngine';
import { getPushStatus, enablePush, disablePush } from './pushNotifications';  

const SUPABASE_URL = "https://trtieaijkrdipiljyzuh.supabase.co";
const SUPABASE_KEY = "sb_publishable_eJKNPdo5XwSCH-X8Ttantw_feBJed2q";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

const SYS = "#38bdf8";
const SYS_BORDER = "rgba(56,189,248,0.25)";
const SYS_DIM = "rgba(56,189,248,0.12)";
const DEFAULT_XP = 10;

function sanitizeName(val){ return (val||"").toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,25); }
function isValidName(val){ return /^[A-Z0-9]{3,25}$/.test((val||"").trim()); }

// Imported from task-tracker — passed as props where needed
// calcL1Threshold, calcTotalXP, xpToLevel are passed in via SettingsXPPage props

// ─── Settings Helper Components ───────────────────────────────────────────────
export function SettingsRow({icon,label,sub,onClick,gold}){
  return(
    <button onClick={onClick} className="btn sys-btn-block"
      style={{display:"flex",alignItems:"center",gap:12,width:"100%",textAlign:"left"}}>
      <span style={{fontSize:20,flexShrink:0}}>{icon}</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:700,color:gold?"#f59e0b":"#c8d8e8"}}>{label}</div>
        <div style={{fontSize:11,color:"#64748b",marginTop:2}}>{sub}</div>
      </div>
      <span style={{color:"#334",fontSize:16,flexShrink:0}}>›</span>
    </button>
  );
}

export function SettingsInfoPage({title,label,children,footer}){
  return(
    <div>
      <div style={{marginBottom:24}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:SYS,letterSpacing:3,marginBottom:4}}>{label}</div>
        <div style={{fontSize:20,fontWeight:800,color:"#fff",
          fontFamily:"'Share Tech Mono',monospace",letterSpacing:2}}>{title}</div>
      </div>
      <div style={{fontSize:13,color:"#94a3b8",lineHeight:1.8,
        display:"flex",flexDirection:"column",gap:12}}>
        <style>{`
          .settings-info h4{font-family:'Share Tech Mono',monospace;font-size:11px;
            letter-spacing:2px;color:${SYS};margin:8px 0 4px;text-transform:uppercase}
          .settings-info p{color:#94a3b8;font-size:13px;line-height:1.8;margin:0}
          .settings-info strong{color:#c8d8e8}
        `}</style>
        <div className="settings-info" style={{display:"flex",flexDirection:"column",gap:8}}>
          {children}
        </div>
      </div>
      {footer&&<div style={{marginTop:24}}>{footer}</div>}
    </div>
  );
}

const WARN = "#ef4444";

export function SettingsUsernamePage({playerName,userId,onRename,isPremium}){
  const [newName,setNewName]=useState("");
  const [nameCheckStatus,setNameCheckStatus]=useState(null); // null|"ok"|"premium_available"|"premium_taken"|"taken"|"checking"
  const [nameMsg,setNameMsg]=useState("");
  const [nameLoading,setNameLoading]=useState(false);

  async function checkNewName(val){
    const v=sanitizeName(val);
    if(!v||v===(playerName||"")){setNameCheckStatus(null);return;}
    setNameCheckStatus("checking");
    const {data}=await supabase.from("premium_usernames").select("*").eq("name",v).single();
    if(!data){
      const {data:taken}=await supabase.from("profiles").select("id").eq("player_name",v).maybeSingle();
      setNameCheckStatus(taken?"taken":"ok");
      return;
    }
    if(data.granted_to===userId){setNameCheckStatus("ok");return;}
    if(!data.granted_to){setNameCheckStatus("premium_available");return;}
    setNameCheckStatus("premium_taken");
  }

  async function applyRename(){
    const v=sanitizeName(newName);
    if(!isValidName(v)){setNameMsg("Name must be 3–25 letters or numbers only.");return;}
    if(nameCheckStatus==="taken"){setNameMsg("Name is taken.");return;}
    setNameLoading(true);
    if(nameCheckStatus==="premium_available"||nameCheckStatus==="premium_taken"){
      const {error}=await supabase.from("username_requests").insert({
        user_id:userId,current_name:playerName,
        requested_name:v,note:"Requested via Settings",status:"pending",
      });
      if(error){setNameMsg("✗ "+error.message);}
      else{setNameMsg("✓ Request sent for ✦ "+v+". Admin will review shortly.");}
      setNameLoading(false);
      return;
    }
    await onRename(v);
    setNameMsg("✓ Name updated to "+v);
    setNewName("");setNameCheckStatus(null);
    setNameLoading(false);
  }

  // eslint-disable-next-line no-unused-vars
  const v=sanitizeName(newName);

  return(
    <div>
      <div style={{marginBottom:24}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:SYS,letterSpacing:3,marginBottom:4}}>[ PLAYER ]</div>
        <div style={{fontSize:20,fontWeight:800,color:"#fff",
          fontFamily:"'Share Tech Mono',monospace",letterSpacing:2}}>CHANGE USERNAME</div>
      </div>

      {/* Current name */}
      <div style={{marginBottom:16}}>
        <div className="sys-label" style={{marginBottom:4}}>CHANGE PLAYER NAME</div>
        <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b"}}>
          Current: <span style={{color:"#fff",fontWeight:700,fontFamily:"'Share Tech Mono',monospace"}}>{playerName}</span>
        </div>
      </div>

      {/* Input */}
      <input value={newName}
        onChange={e=>{
          const val=sanitizeName(e.target.value);
          setNewName(val);setNameMsg("");setNameCheckStatus(null);
          if(val.trim().length>=2){
            clearTimeout(window._rnTimer);
            window._rnTimer=setTimeout(()=>checkNewName(val),400);
          }
        }}
        placeholder="NEW NAME..."
        style={{width:"100%",background:"rgba(56,189,248,0.05)",
          border:`1px solid ${nameCheckStatus==="ok"?"rgba(74,222,128,0.4)":nameCheckStatus==="taken"||nameCheckStatus==="premium_taken"?"rgba(239,68,68,0.4)":nameCheckStatus==="premium_available"?"rgba(74,222,128,0.5)":SYS_BORDER}`,
          borderRadius:10,padding:"11px 14px",color:"#fff",fontSize:14,fontWeight:700,
          fontFamily:"'Share Tech Mono',monospace",outline:"none",marginBottom:8,
          letterSpacing:2,boxSizing:"border-box"}}/>

      {nameCheckStatus==="checking"&&(
        <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:8,letterSpacing:1}}>· · · checking · · ·</div>
      )}
      {nameCheckStatus==="ok"&&(
        <div style={{fontFamily:"monospace",fontSize:10,color:"#4ade80",marginBottom:8}}>✓ Name is available</div>
      )}

      {/* Premium available */}
      {nameCheckStatus==="premium_available"&&(
        <div style={{borderRadius:10,overflow:"hidden",marginBottom:10}}>
          <div style={{background:"linear-gradient(90deg,rgba(74,222,128,0.15),rgba(74,222,128,0.05))",
            border:"1px solid rgba(74,222,128,0.3)",padding:"10px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:"#4ade80",letterSpacing:2,fontWeight:700}}>✦ PREMIUM NAME</span>
              <span style={{fontFamily:"monospace",fontSize:9,color:"#4ade80",letterSpacing:1}}>AVAILABLE</span>
            </div>
            <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",lineHeight:1.6}}>
              This is a reserved name. Requesting it will notify the admin — once payment is confirmed your name will be updated.
              You'll keep your current name in the meantime.
            </div>
          </div>
        </div>
      )}

      {/* Premium taken */}
      {nameCheckStatus==="premium_taken"&&(
        <div style={{borderRadius:10,overflow:"hidden",marginBottom:10}}>
          <div style={{background:"rgba(239,68,68,0.07)",
            border:"1px solid rgba(239,68,68,0.25)",padding:"10px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:WARN,letterSpacing:2,fontWeight:700}}>✦ PREMIUM NAME</span>
              <span style={{fontFamily:"monospace",fontSize:9,color:WARN,letterSpacing:1}}>TAKEN</span>
            </div>
            <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",lineHeight:1.6}}>
              This name is owned by another player. You can still request it — admin will review.
            </div>
          </div>
        </div>
      )}

      {nameCheckStatus==="taken"&&(
        <div style={{fontFamily:"monospace",fontSize:10,color:WARN,marginBottom:8}}>✗ Name not available</div>
      )}
      {nameMsg&&(
        <div style={{fontFamily:"monospace",fontSize:10,marginBottom:10,
          color:nameMsg.startsWith("✓")?"#4ade80":WARN}}>{nameMsg}</div>
      )}

      <button onClick={applyRename}
        disabled={nameLoading||!newName.trim()||nameCheckStatus==="taken"||nameCheckStatus==="checking"}
        style={{width:"100%",padding:"11px",
          background:nameCheckStatus==="premium_available"?"rgba(74,222,128,0.15)"
            :nameCheckStatus==="premium_taken"?"rgba(245,158,11,0.1)"
            :"rgba(56,189,248,0.1)",
          border:`1px solid ${nameCheckStatus==="premium_available"?"rgba(74,222,128,0.4)":nameCheckStatus==="premium_taken"?"rgba(245,158,11,0.3)":SYS_BORDER}`,
          borderRadius:10,
          color:nameCheckStatus==="premium_available"?"#4ade80":nameCheckStatus==="premium_taken"?"#f59e0b":SYS,
          fontFamily:"monospace",fontSize:11,cursor:"pointer",letterSpacing:2,
          opacity:(nameLoading||!newName.trim())?"0.4":"1"}}>
        {nameLoading?"UPDATING..."
          :nameCheckStatus==="premium_available"?"[ ✦ REQUEST THIS NAME ]"
          :nameCheckStatus==="premium_taken"?"[ REQUEST ANYWAY ]"
          :"[ UPDATE NAME ]"}
      </button>

      <div style={{fontSize:11,color:"#64748b",marginTop:12,lineHeight:1.6,marginBottom:24}}>
        Names must be 3–25 characters, letters and numbers only, uppercase.
        Premium names require admin approval.
      </div>

      {isPremium?(
        <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.25)",
          borderRadius:12,padding:"16px",display:"flex",alignItems:"center",gap:12}}>
          <img src="/swords/sword_1.png" alt=""
            style={{width:24,height:24,objectFit:"contain",
              filter:"drop-shadow(0 0 6px rgba(251,191,36,0.9))",
              mixBlendMode:"screen",flexShrink:0}}/>
          <div>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,
              color:"#fbbf24",letterSpacing:2,marginBottom:2}}>✦ PREMIUM USERNAME</div>
            <div style={{fontSize:11,color:"#94a3b8",lineHeight:1.6}}>
              Your name has premium status — golden shimmer and sword prefix are active.
            </div>
          </div>
        </div>
      ):(
        <PremiumRequestBox playerName={playerName} userId={userId}/>
      )}
    </div>
  );
}

function PremiumRequestBox({playerName, userId}){
  const [reqNote,setReqNote]=useState("");
  const [reqSent,setReqSent]=useState(false);
  const [reqLoading,setReqLoading]=useState(false);
  const [reqError,setReqError]=useState("");
  const [showReqForm,setShowReqForm]=useState(false);

  async function requestCurrentNameAsPremium(){
    if(!reqNote.trim()){setReqError("Add a note (e.g. payment method).");return;}
    setReqLoading(true);setReqError("");
    try{
      await supabase.from("username_requests").insert({
        user_id:userId,
        current_name:playerName,
        requested_name:playerName,
        note:"[MAKE CURRENT NAME PREMIUM] "+reqNote.trim(),
        status:"pending",
      });
      setReqSent(true);
    }catch(e){setReqError(e.message||"Failed to send.");}
    setReqLoading(false);
  }

  return(
    <div style={{background:"rgba(251,191,36,0.05)",border:"1px solid rgba(251,191,36,0.2)",
      borderRadius:12,padding:"16px"}}>
      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,
        color:"#fbbf24",letterSpacing:3,marginBottom:10}}>[ WANT SOMETHING SPECIAL? ]</div>
      <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.7,marginBottom:14}}>
        Premium usernames come with a <strong style={{color:"#fbbf24"}}>golden shimmer</strong> and
        an <strong style={{color:"#fbbf24"}}>animated sword prefix</strong>. Here's how your name could look:
      </div>
      <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.2)",
        borderRadius:10,padding:"14px 16px",marginBottom:14,
        display:"flex",alignItems:"center",gap:10}}>
        <img src="/swords/sword_1.png" alt=""
          style={{width:22,height:22,objectFit:"contain",
            filter:"drop-shadow(0 0 6px rgba(251,191,36,0.9))",
            mixBlendMode:"screen",flexShrink:0}}/>
        <style>{`@keyframes goldShimmerPrev{0%{background-position:200% center}100%{background-position:-200% center}}`}</style>
        <span style={{
          fontFamily:"'Share Tech Mono',monospace",fontSize:18,fontWeight:900,
          background:"linear-gradient(90deg,#92400e,#fbbf24,#fef3c7,#fbbf24,#d97706,#fbbf24,#92400e)",
          backgroundSize:"200% auto",
          WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",
          animation:"goldShimmerPrev 3s linear infinite",
        }}>
          {playerName}
        </span>
      </div>
      {reqSent?(
        <div style={{textAlign:"center",padding:"12px",background:"rgba(74,222,128,0.06)",
          border:"1px solid rgba(74,222,128,0.2)",borderRadius:10,
          fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:"#4ade80",letterSpacing:1}}>
          ✓ REQUEST SENT — Admin will review and confirm payment
        </div>
      ):showReqForm?(
        <div>
          <div style={{fontSize:11,color:"#94a3b8",marginBottom:8,lineHeight:1.6}}>
            Add a note for the admin — include your preferred payment method:
          </div>
          <textarea className="field" value={reqNote}
            onChange={e=>{setReqNote(e.target.value);setReqError("");}}
            placeholder="e.g. UPI / PayPal / other — I want my current name as premium"
            style={{minHeight:72,resize:"vertical",marginBottom:reqError?6:10}}/>
          {reqError&&<div style={{fontSize:11,color:"#ef4444",marginBottom:8}}>{reqError}</div>}
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowReqForm(false)}
              style={{flex:1,padding:"10px",background:"none",
                border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,
                color:"#64748b",fontFamily:"'Share Tech Mono',monospace",fontSize:10,cursor:"pointer"}}>
              CANCEL
            </button>
            <button onClick={requestCurrentNameAsPremium} disabled={reqLoading}
              style={{flex:2,padding:"10px",background:"rgba(251,191,36,0.12)",
                border:"1px solid rgba(251,191,36,0.3)",borderRadius:8,
                color:"#fbbf24",fontFamily:"'Share Tech Mono',monospace",
                fontSize:10,letterSpacing:1,cursor:"pointer",fontWeight:700}}>
              {reqLoading?"SENDING...":"✦ SUBMIT REQUEST"}
            </button>
          </div>
        </div>
      ):(
        <div>
          <div style={{fontSize:11,color:"#64748b",lineHeight:1.6,marginBottom:12}}>
            You can also request a pre-reserved legendary name by typing it in the field above.
          </div>
          <div style={{fontSize:11,color:"#64748b",lineHeight:1.6,marginBottom:12,
            background:"rgba(251,191,36,0.04)",border:"1px solid rgba(251,191,36,0.12)",
            borderRadius:8,padding:"10px 12px"}}>
            After submitting, the admin will contact you with payment details. Your name will be updated once payment is confirmed.
          </div>
          <button onClick={()=>setShowReqForm(true)}
            style={{width:"100%",padding:"12px",background:"rgba(251,191,36,0.1)",
              border:"1px solid rgba(251,191,36,0.3)",borderRadius:10,
              color:"#fbbf24",fontFamily:"'Share Tech Mono',monospace",
              fontSize:11,letterSpacing:2,cursor:"pointer",fontWeight:700}}>
            ✦ REQUEST "{playerName}" AS PREMIUM
          </button>
        </div>
      )}
    </div>
  );
}

const CAT_ICONS = {work:"💼",personal:"🌱",health:"💪",learning:"📚",creative:"🎨",other:"⚡"};

function getEffectiveGoalAmtLocal(task, period){
  const g = task.goals?.[period];
  if(!g) return 0;
  if(task.metric==="time") return (parseInt(g.h||0)*60)+parseInt(g.m||0);
  return parseFloat(g.amount||0);
}

function xpForLogLocal(task, log){
  const weeklyXP = task.xp || DEFAULT_XP;
  const goal = getEffectiveGoalAmtLocal(task,"weekly") || getEffectiveGoalAmtLocal(task,"daily")*7;
  if(!goal||goal<=0) return weeklyXP;
  let value = 0;
  if(task.metric==="time") value = log.value?.totalMins||0;
  else value = parseFloat(log.value?.amount||0);
  if(value<=0) return 0;
  return (value/goal)*weeklyXP;
}

export function SettingsXPPage({tasks,onSave,calcL1Threshold,calcTotalXP,xpToLevel}){
  const [xpMap,setXpMap]=useState(()=>Object.fromEntries(tasks.map(t=>[t.id,String(t.xp||DEFAULT_XP)])));
  const previewTasks=tasks.map(t=>({...t,xp:parseInt(xpMap[t.id])||DEFAULT_XP}));
  const base=calcL1Threshold(previewTasks);
  const {level,xpIntoLevel,xpForNext,pct}=xpToLevel(calcTotalXP(previewTasks),base);
  const totalXP=calcTotalXP(previewTasks);

  return(
    <div>
      <div style={{marginBottom:20}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:SYS,letterSpacing:3,marginBottom:4}}>[ PLAYER ]</div>
        <div style={{fontSize:20,fontWeight:800,color:"#fff",
          fontFamily:"'Share Tech Mono',monospace",letterSpacing:2}}>SETUP XP</div>
      </div>

      {/* Level card */}
      <div style={{background:"rgba(56,189,248,0.06)",border:`1px solid ${SYS_BORDER}`,
        borderRadius:12,padding:"16px",marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
          <div>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,
              color:SYS,letterSpacing:3,marginBottom:4}}>CURRENT LEVEL</div>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:40,
              fontWeight:900,color:SYS,lineHeight:1}}>{level}</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:1,marginBottom:4}}>TOTAL XP</div>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:22,
              fontWeight:800,color:"#fff"}}>{Math.floor(totalXP)}</div>
            <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",marginTop:2}}>Lv1→2: {base} XP</div>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontFamily:"monospace",
          fontSize:10,color:"#64748b",marginBottom:6}}>
          <span>{xpIntoLevel} / {xpForNext} XP → Level {level+1}</span>
          <span>{pct}%</span>
        </div>
        <div style={{height:4,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden"}}>
          <div style={{height:"100%",borderRadius:2,width:`${pct}%`,
            background:`linear-gradient(90deg,${SYS},#818cf8)`,transition:"width 0.4s"}}/>
        </div>
      </div>

      {/* How XP works */}
      <div style={{padding:"10px 14px",background:"rgba(56,189,248,0.04)",
        border:`1px solid ${SYS_BORDER}`,borderRadius:10,marginBottom:16,
        fontFamily:"monospace",fontSize:10,color:"#64748b",lineHeight:1.8}}>
        <span style={{color:SYS,fontWeight:700}}>How XP works:</span> Each target has a weekly goal. Set the XP below — it gets split across your goal units. Every log earns its share. Hit all targets and you <span style={{color:SYS,fontWeight:700}}>LEVEL UP</span>.
      </div>

      {/* XP ALLOCATION label */}
      <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",
        letterSpacing:2,marginBottom:10}}>[ XP ALLOCATION ]</div>

      {/* Per-task cards */}
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
        {tasks.map(t=>{
          const weeklyXP=parseInt(xpMap[t.id])||DEFAULT_XP;
          const totalAlloc=Object.values(xpMap).reduce((s,v)=>s+(parseInt(v)||0),0)||1;
          const sharePct=Math.round((weeklyXP/totalAlloc)*10000)/100;
          const goal=getEffectiveGoalAmtLocal(t,"weekly")||getEffectiveGoalAmtLocal(t,"daily")*7;
          const earned=Math.floor((t.logs||[]).reduce((s,log)=>s+xpForLogLocal({...t,xp:weeklyXP},log),0));
          const perLog=goal>0?Math.round((weeklyXP/goal)*100)/100:weeklyXP;
          const icon=CAT_ICONS[t.cat]||"⚡";
          return(
            <div key={t.id} style={{background:"rgba(255,255,255,0.02)",
              border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,padding:"10px 14px"}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:4}}>
                    <span style={{fontSize:12}}>{icon}</span>
                    <span style={{fontWeight:700,color:"#ddeeff",fontSize:13}}>{t.title}</span>
                    <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,
                      color:"#4ade80",fontWeight:700,marginLeft:2}}>{sharePct}%</span>
                  </div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",lineHeight:1.7}}>
                    {perLog} XP/unit · {(t.logs||[]).length} logs
                    {goal>0&&<><br/><span style={{color:"#64748b"}}>weekly goal: {goal}</span></>}
                    <br/><span style={{color:SYS}}>{earned} XP earned</span>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                  <button onClick={()=>setXpMap(prev=>({...prev,[t.id]:String(Math.max(5,(parseInt(prev[t.id])||DEFAULT_XP)-5))}))}
                    style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",
                      color:"#94a3b8",borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:14,fontWeight:700}}>−</button>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:16,fontWeight:800,
                    color:SYS,minWidth:36,textAlign:"center"}}>{weeklyXP}</div>
                  <button onClick={()=>setXpMap(prev=>({...prev,[t.id]:String((parseInt(prev[t.id])||DEFAULT_XP)+5)}))}
                    style={{background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,
                      color:SYS,borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:14,fontWeight:700}}>+</button>
                </div>
              </div>
              <div style={{marginTop:8,height:3,background:"rgba(255,255,255,0.04)",borderRadius:2,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${sharePct}%`,borderRadius:2,
                  background:"linear-gradient(90deg,#4ade80,#86efac)",transition:"width 0.4s ease"}}/>
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={()=>onSave(xpMap)}
        style={{width:"100%",padding:"13px",background:SYS_DIM,
          border:`1px solid ${SYS_BORDER}`,borderRadius:10,color:SYS,
          fontFamily:"'Share Tech Mono',monospace",fontSize:12,
          letterSpacing:2,cursor:"pointer",fontWeight:700}}>
        SAVE XP SETTINGS
      </button>
    </div>
  );
}

export function SettingsContactPage({userId,playerName,email,sysMessages,userReplies,onOpenInbox}){
  const [msg,setMsg]=useState("");
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [localReplies,setLocalReplies]=useState(userReplies||[]);
  const threadEndRef=useRef(null);

  const directMsgs=(sysMessages||[]).filter(m=>m.recipient_id===userId);
  const thread=[
    ...directMsgs.map(m=>({type:"admin",text:m.message,date:m.created_at,id:m.id})),
    ...localReplies.map(r=>({type:"user",text:r.reply||r.text||"",date:r.created_at||r.date,id:r.id})),
  ].sort((a,b)=>new Date(a.date)-new Date(b.date));

  // Scroll to bottom when thread updates
  useEffect(()=>{
    threadEndRef.current?.scrollIntoView({behavior:"smooth"});
  },[thread.length]);

  async function sendMessage(){
    if(!msg.trim()){setError("Enter a message.");return;}
    setLoading(true);setError("");
    try{
      await supabase.from("user_replies").insert({
        message_id:null,
        user_id:userId,
        player_name:playerName,
        email:email||null,
        reply:msg.trim(),
      });
      setLocalReplies(prev=>[...prev,{
        type:"user",text:msg.trim(),
        date:new Date().toISOString(),id:Date.now()
      }]);
      setMsg("");
    }catch(e){setError(e.message||"Failed to send.");}
    setLoading(false);
  }

  return(
    <div>
      <div style={{marginBottom:20}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:SYS,letterSpacing:3,marginBottom:4}}>[ SUPPORT ]</div>
        <div style={{fontSize:20,fontWeight:800,color:"#fff",
          fontFamily:"'Share Tech Mono',monospace",letterSpacing:2}}>CONTACT US</div>
      </div>

      {/* Conversation history */}
      <div style={{background:"rgba(255,255,255,0.02)",border:`1px solid ${SYS_BORDER}`,
        borderRadius:12,overflow:"hidden",marginBottom:16}}>
        <div style={{padding:"12px 14px",borderBottom:"1px solid rgba(255,255,255,0.05)",
          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,
            color:SYS,letterSpacing:3}}>[ CONVERSATION ]</div>
          {thread.length>0&&(
            <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b"}}>
              {thread.length} message{thread.length!==1?"s":""}
            </div>
          )}
        </div>

        <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:8,
          maxHeight:320,overflowY:"auto"}}>
          {thread.length===0&&(
            <div style={{textAlign:"center",padding:"24px 0",
              fontFamily:"monospace",fontSize:11,color:"#64748b"}}>
              No messages yet — send one below
            </div>
          )}
          {thread.map((item,i)=>(
            <div key={i} style={{display:"flex",
              justifyContent:item.type==="admin"?"flex-start":"flex-end"}}>
              <div style={{maxWidth:"85%",padding:"9px 12px",borderRadius:10,
                background:item.type==="admin"?"rgba(56,189,248,0.08)":"rgba(74,222,128,0.07)",
                border:`1px solid ${item.type==="admin"?"rgba(56,189,248,0.15)":"rgba(74,222,128,0.15)"}`}}>
                <div style={{fontFamily:"monospace",fontSize:9,
                  color:item.type==="admin"?SYS:"#4ade80",
                  marginBottom:4,letterSpacing:1}}>
                  {item.type==="admin"?"ADMIN":"YOU"}
                </div>
                <div style={{fontFamily:"monospace",fontSize:12,color:"#ddeeff",
                  lineHeight:1.6,whiteSpace:"pre-wrap"}}>{item.text.replace(/^\[CONTACT US\]\s*/,"")}</div>
                <div style={{fontFamily:"monospace",fontSize:8,color:"#64748b",marginTop:3}}>
                  {new Date(item.date).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                </div>
              </div>
            </div>
          ))}
          <div ref={threadEndRef}/>
        </div>

        {/* Compose box inside thread */}
        <div style={{padding:"10px 14px",borderTop:"1px solid rgba(255,255,255,0.05)",
          background:"rgba(0,0,0,0.15)"}}>
          <textarea className="field" value={msg}
            onChange={e=>{setMsg(e.target.value);setError("");}}
            placeholder="Type your message..."
            style={{minHeight:72,resize:"vertical",marginBottom:error?6:8}}/>
          {error&&<div style={{fontSize:11,color:"#ef4444",marginBottom:8}}>{error}</div>}
          <button onClick={sendMessage} disabled={loading||!msg.trim()}
            style={{width:"100%",padding:"11px",background:SYS_DIM,
              border:`1px solid ${SYS_BORDER}`,borderRadius:8,color:SYS,
              fontFamily:"'Share Tech Mono',monospace",fontSize:11,
              letterSpacing:2,cursor:"pointer",fontWeight:700,
              opacity:msg.trim()?1:0.5}}>
            {loading?"SENDING...":"✓ SEND MESSAGE"}
          </button>
        </div>
      </div>

    </div>
  );
}
export function SettingsNotificationsPage({ userId }) {
  const [status, setStatus] = useState("checking"); // checking | off | on | denied | unsupported
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    getPushStatus().then(setStatus);
  }, []);

  async function enable() {
    setBusy(true); setErr("");
    try {
      const result = await enablePush(supabase, userId);
      if (result !== "granted") {
        setStatus(result === "denied" ? "denied" : "off");
        setBusy(false);
        return;
      }
      AudioEngine.click();
      setStatus("on");
    } catch (e) {
      setErr(e.message || "Couldn't enable notifications.");
    }
    setBusy(false);
  }

  async function disable() {
    setBusy(true); setErr("");
    try {
      await disablePush(supabase);
      AudioEngine.click();
      setStatus("off");
    } catch (e) {
      setErr(e.message || "Couldn't disable notifications.");
    }
    setBusy(false);
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 10,
          color: SYS, letterSpacing: 3, marginBottom: 4 }}>[ SYSTEM ]</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#fff',
          fontFamily: "'Share Tech Mono',monospace", letterSpacing: 2 }}>NOTIFICATIONS</div>
      </div>

      <div style={{ background: 'rgba(56,189,248,0.05)', border: `1px solid ${SYS_BORDER}`,
        borderRadius: 12, padding: '20px 18px', marginBottom: 16 }}>
        <div style={{ fontFamily: "monospace", fontSize: 12, color: "#94a3b8",
          lineHeight: 1.6, marginBottom: 18 }}>
          Get reminded about pending targets at <span style={{color:SYS}}>7 AM</span> and{" "}
          <span style={{color:SYS}}>5 PM</span> your local time.
        </div>

        {status === "checking" && (
          <div style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b" }}>Checking…</div>
        )}

        {status === "unsupported" && (
          <div style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b" }}>
            Your browser doesn't support push notifications.
          </div>
        )}

        {status === "denied" && (
          <div style={{ fontFamily: "monospace", fontSize: 11, color: "#ef4444", lineHeight: 1.6 }}>
            Notifications are blocked for this app. Enable them from your device's app settings,
            then come back here.
          </div>
        )}

        {(status === "on" || status === "off") && (
          <button onClick={status === "on" ? disable : enable} disabled={busy}
            style={{ width: "100%", padding: "12px", borderRadius: 10, cursor: "pointer",
              fontFamily: "'Share Tech Mono',monospace", fontSize: 12, fontWeight: 700,
              letterSpacing: 2, opacity: busy ? 0.6 : 1,
              background: status === "on" ? "rgba(239,68,68,0.1)" : SYS,
              border: status === "on" ? "1px solid rgba(239,68,68,0.3)" : "none",
              color: status === "on" ? "#ef4444" : "#000" }}>
            {busy ? "…" : status === "on" ? "⊘ DISABLE" : "🔔 ENABLE"}
          </button>
        )}

        {err && <div style={{ fontFamily: "monospace", fontSize: 10, color: "#ef4444",
          marginTop: 10 }}>{err}</div>}
      </div>
    </div>
  );
}


export function SettingsAudioPage() {
  const [vol, setVol] = useState(() =>
    parseFloat(localStorage.getItem('arise_volume') ?? '0.7')
  );
  const [prevVol, setPrevVol] = useState(vol > 0 ? vol : 0.7);
  const isMuted = vol === 0;
  const previewTimer = useRef(null);

  function handleSlider(e) {
    const v = parseFloat(e.target.value);
    setVol(v);
    if (v > 0) setPrevVol(v);
    AudioEngine.setVolume(v);

    // Debounced preview tone — fires once ~150ms after the user stops
    // dragging/adjusting, so a continuous drag doesn't spam sound calls.
    // Covers both mouse drag and keyboard (arrow key) adjustment.
    clearTimeout(previewTimer.current);
    if (v > 0) {
      previewTimer.current = setTimeout(() => {
        AudioEngine.click();
      }, 150);
    }
  }

  function toggleMute() {
    const next = isMuted ? prevVol : 0;
    setVol(next);
    AudioEngine.setVolume(next);
    if (next > 0) AudioEngine.click(); // confirm the restored level; stay silent when muting
  }

  const pct = Math.round(vol * 100);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 10,
          color: SYS, letterSpacing: 3, marginBottom: 4 }}>[ SYSTEM ]</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#fff',
          fontFamily: "'Share Tech Mono',monospace", letterSpacing: 2 }}>AUDIO</div>
      </div>

      <div style={{ background: 'rgba(56,189,248,0.05)', border: `1px solid ${SYS_BORDER}`,
        borderRadius: 12, padding: '20px 18px', marginBottom: 16 }}>

        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 9,
              color: SYS, letterSpacing: 3, marginBottom: 4 }}>VOLUME LEVEL</div>
            <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 36,
              fontWeight: 900, color: isMuted ? '#334455' : SYS, lineHeight: 1,
              transition: 'color 0.2s' }}>
              {isMuted ? 'MUTE' : `${pct}%`}
            </div>
          </div>

          <button onClick={toggleMute} style={{
            background: isMuted ? 'rgba(239,68,68,0.12)' : SYS_DIM,
            border: `1px solid ${isMuted ? 'rgba(239,68,68,0.35)' : SYS_BORDER}`,
            borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
            fontFamily: "'Share Tech Mono',monospace", fontSize: 11,
            color: isMuted ? '#ef4444' : SYS, letterSpacing: 2, fontWeight: 700,
            transition: 'all 0.2s'
          }}>
            {isMuted ? '⊘ MUTED' : '🔊 MUTE'}
          </button>
        </div>

        {/* Slider */}
        <style>{`
          .arise-vol-slider {
            -webkit-appearance: none; appearance: none;
            width: 100%; height: 4px; border-radius: 2px; outline: none; cursor: pointer;
            background: linear-gradient(90deg,
              ${isMuted ? '#334455' : '#38bdf8'} ${pct}%,
              rgba(255,255,255,0.07) ${pct}%);
            transition: background 0.1s;
          }
          .arise-vol-slider::-webkit-slider-thumb {
            -webkit-appearance: none; appearance: none;
            width: 20px; height: 20px; border-radius: 50%;
            background: ${isMuted ? '#334455' : '#38bdf8'};
            border: 2px solid ${isMuted ? '#475569' : 'rgba(56,189,248,0.6)'};
            box-shadow: ${isMuted ? 'none' : '0 0 8px rgba(56,189,248,0.5)'};
            transition: all 0.2s; cursor: pointer;
          }
        `}</style>
        <input
          type="range" min="0" max="1" step="0.05"
          value={vol}
          onChange={handleSlider}
          className="arise-vol-slider"
          style={{ opacity: isMuted ? 0.35 : 1 }}
        />

        {/* Tick labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
          fontFamily: 'monospace', fontSize: 9, color: '#475569', marginTop: 6 }}>
          {['0%', '25%', '50%', '75%', '100%'].map(l => <span key={l}>{l}</span>)}
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: '10px 14px', background: 'rgba(56,189,248,0.04)',
        border: `1px solid ${SYS_BORDER}`, borderRadius: 10,
        fontFamily: 'monospace', fontSize: 10, color: '#64748b', lineHeight: 1.8 }}>
        <span style={{ color: SYS, fontWeight: 700 }}>Default is 70%.</span>
        {' '}Drag to set your preferred volume. Mute turns off all sounds while keeping
        your setting saved for when you unmute.
      </div>
    </div>
  );
}

